// This page will be rendered when an error makes it all the way to the top of the
// application without being handled by a Javascript catch statement or React error
// boundary.

const stars = Array.from({ length: 100 }, () => ({
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 2 + 1,
  opacity: Math.random() * 0.8 + 0.2,
}));

const meteors = Array.from({ length: 15 }, (_, i) => {
  const startPosition = Math.random() * 100;
  const isTopEdge = Math.random() > 0.5;

  return {
    x: isTopEdge ? startPosition : -5,
    y: isTopEdge ? -5 : startPosition,
    size: Math.random() * 10 + 5,
    speed: Math.random() * 5 + 2,
    angle: Math.random() * 60 - 30,
    delay: i * 0.2,
    fromTop: isTopEdge,
  };
});

export default ({ error }) => {
  if (
    error?.message?.includes("Failed to fetch dynamically imported module") &&
    !window.location.href.includes("reload=1")
  ) {
    if (window.location.href.includes("?")) {
      window.location.href += "&reload=1";
    } else {
      window.location.href += "?reload=1";
    }
    return;
  }

  return (
    <div className="relative h-screen w-full overflow-hidden bg-black">
      <style
        dangerouslySetInnerHTML={{
          __html: `
              @keyframes glitch {
                0% {
                  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
                  transform: translate(0, 0);
                }
                5% {
                  clip-path: polygon(0 2%, 100% 2%, 100% 5%, 0 5%);
                  transform: translate(-5px, 0);
                }
                6% {
                  clip-path: polygon(0 15%, 100% 15%, 100% 15%, 0 15%);
                  transform: translate(5px, 0);
                }
                7% {
                  clip-path: polygon(0 10%, 100% 10%, 100% 20%, 0 20%);
                  transform: translate(-3px, 0);
                }
                8% {
                  clip-path: polygon(0 1%, 100% 1%, 100% 2%, 0 2%);
                  transform: translate(0, 0);
                }
                10% {
                  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
                  transform: translate(0, 0);
                }
                80% {
                  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
                  transform: translate(0, 0);
                }
                83% {
                  clip-path: polygon(0 50%, 100% 50%, 100% 55%, 0 55%);
                  transform: translate(-5px, 0);
                }
                84% {
                  clip-path: polygon(0 80%, 100% 80%, 100% 80%, 0 80%);
                  transform: translate(5px, 0);
                }
                85% {
                  clip-path: polygon(0 50%, 100% 50%, 100% 20%, 0 20%);
                  transform: translate(0, 0);
                }
                86% {
                  clip-path: polygon(0 33%, 100% 33%, 100% 33%, 0 33%);
                  transform: translate(3px, 0);
                }
                87% {
                  clip-path: polygon(0 44%, 100% 44%, 100% 44%, 0 44%);
                  transform: translate(0, 0);
                }
                90% {
                  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
                  transform: translate(0, 0);
                }
                100% {
                  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
                  transform: translate(0, 0);
                }
              }
              @keyframes meteor {
                0% {
                  transform: translate(0, 0) rotate(0deg);
                  opacity: 0;
                }
                5% {
                  opacity: 0.8;
                }
                95% {
                  opacity: 0.8;
                }
                100% {
                  transform: translate(120vw, 120vh) rotate(20deg);
                  opacity: 0;
                }
              }
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.3; }
              }
              @keyframes warning-blink {
                0%, 49% { background-color: var(--destructive); box-shadow: 0 0 20px var(--destructive); opacity: 0.8; filter: blur(1px); }
                50%, 100% { background-color: #4e0a09; box-shadow: 0 0 5px #4e0a09; }
              }
              .error-code {
                animation: glitch 10s infinite;
              }
              .error-code::after {
                content: "500";
                position: absolute;
                left: 3px;
                top: 0;
                color: #00ffff;
                text-shadow: 0 0 5px #00ffff, 0 0 10px #00ffff;
                opacity: 0.5;
                animation: glitch 10s infinite reverse;
              }
              .error-code::before {
                content: "500";
                position: absolute;
                left: -3px;
                top: 0;
                color: #ff00ff;
                text-shadow: 0 0 5px #ff00ff, 0 0 10px #ff00ff;
                opacity: 0.5;
                animation: glitch 10s infinite;
              }
              .warning-light {
                animation: warning-blink 1s infinite;
              }
              .meteor {
                will-change: transform;
                animation: meteor var(--speed) linear infinite;
                animation-delay: var(--delay);
              }
            `,
        }}
      />

      {/* Stars */}
      <div className="fixed inset-0 z-0 bg-black">
        {stars.map((star, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              top: `${star.y}%`,
              left: `${star.x}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity,
            }}
          />
        ))}
      </div>

      {/* Glitching 500 Error Code */}
      <div className="error-code text-destructive absolute top-[25%] left-1/2 -translate-x-1/2 -translate-y-1/2 font-mono text-[30vw] font-black opacity-90 md:top-[30%] md:text-[15rem]">
        500
      </div>

      {/* Alert Message and Controls */}
      <div className="absolute top-[55%] left-1/2 z-10 w-[90%] max-w-[600px] -translate-x-1/2 -translate-y-1/2 text-center">
        <div className="mb-4 flex justify-center">
          <div className="warning-light mx-2.5 inline-block h-5 w-5 rounded-full"></div>
          <div className="warning-light mx-2.5 inline-block h-5 w-5 rounded-full"></div>
          <div className="warning-light mx-2.5 inline-block h-5 w-5 rounded-full"></div>
        </div>
        <div className="mb-8 font-mono text-3xl font-bold text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.7)]">
          CRITICAL SYSTEM FAILURE
        </div>
        <div className="mb-8 font-mono text-base leading-relaxed text-gray-300">
          Our ship has encountered an unexpected cosmic anomaly. The issue has
          been logged, and our engineers are working on it. Please remain calm
          and follow the emergency protocols.
        </div>
        <div className="mt-5 flex justify-center gap-2 md:gap-4">
          <button
            onClick={() => (window.location.href = "/dashboard")}
            className="rounded-md border-2 border-[#00ffff] bg-black/70 px-3 py-1.5 font-mono text-sm font-bold text-[#00ffff] shadow-[0_0_10px_rgba(0,255,255,0.3)] transition-all duration-300 hover:bg-[rgba(0,255,255,0.2)] hover:shadow-[0_0_15px_rgba(0,255,255,0.5)] md:px-5 md:py-2.5 md:text-base"
          >
            RETURN HOME
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md border-2 border-[#00ffff] bg-black/70 px-3 py-1.5 font-mono text-sm font-bold text-[#00ffff] shadow-[0_0_10px_rgba(0,255,255,0.3)] transition-all duration-300 hover:bg-[rgba(0,255,255,0.2)] hover:shadow-[0_0_15px_rgba(0,255,255,0.5)] md:px-5 md:py-2.5 md:text-base"
          >
            RELOAD
          </button>
        </div>
      </div>

      {/* Meteor debris */}
      {meteors.map((meteor, i) => (
        <div
          key={i}
          className="meteor absolute z-5 rounded-full bg-gradient-to-r from-[#ff3635] to-[#ff8a35] opacity-0 shadow-[0_0_20px_rgba(255,54,53,0.8)]"
          style={{
            width: `${meteor.size}px`,
            height: `${meteor.size}px`,
            top: `${meteor.y}%`,
            left: `${meteor.x}%`,
            transform: `rotate(${meteor.angle}deg)`,
            "--speed": `${meteor.speed}s`,
            "--delay": `${meteor.delay}s`,
          }}
        />
      ))}
    </div>
  );
};
